import { apiSlice } from './apiSlice';
import { t } from '../../i18n/i18n';

/**
 * Affiliate programme — the user side of "Alpha Capital — Affiliate System"
 * (Postman). Every path ends in a slash, although the collection leaves some
 * off: without it Django answers 301, which Postman follows quietly but a
 * browser can't use — a POST that is redirected comes back as a GET, so the
 * retainer request would never be made.
 *
 * Dashboard page
 *   GET  /affiliate/dashboard/cards/           range=1d|1w|1m|3m|ytd|all, or start_date + end_date
 *   GET  /affiliate/dashboard/trend/           same period + metric=all|revenue|clicks|sales|signups
 *   GET  /affiliate/dashboard/recent-requests/ request_type, status, search, page, page_size
 *
 * Requests (giveaways / personal accounts)
 *   GET  /affiliate/requests/summary/          eligibility, counts per type/status, retainer state
 *   GET  /affiliate/requests/options/          platforms, challenges, limits for the forms
 *   GET  /affiliate/requests/                  request_type, status, search, page, page_size
 *   GET  /affiliate/requests/<id>/             one request with its winner_list
 *   POST /affiliate/requests/                  { request_type, challenge, platform, quantity, purpose, end_date? }
 *
 * Winners
 *   GET  /affiliate/giveaways/approved/        giveaways winners can be submitted for
 *   POST /affiliate/giveaways/<id>/winners/    { winners: [email, …] }
 *   GET  /affiliate/giveaways/<id>/winners/    one giveaway's winners
 *   GET  /affiliate/winners/                   every winner: giveaway, status, search, page
 *
 * Retainer
 *   GET  /affiliate/retainer/                  terms, request window, payouts
 *   POST /affiliate/retainer/request/          { reason? }
 *
 * Affiliate Marketing — the live dashboard's Overview / Affiliate Codes /
 * Commission Payouts tabs (`AffiliatePage.jsx` on main), on its own endpoints
 *   GET  /affiliate/v2/dashboard/              start_date + end_date as DD/MMM/YYYY; withdrawal terms, bonus, super_affiliate
 *   GET  /affiliate/v2/code/list/              page, page_size, search
 *   GET  /affiliate/get/code/referred-users/list/  affiliate_code_id, page, page_size
 *   GET  /affiliate/get/countrywise-code/list/     affiliate_code_id — code_name, totals, countries
 *   GET  /affiliate/v2/commission/payment/     page, page_size, search
 *
 * Super Affiliate
 *   POST /affiliate/super-affiliate/application/              the application form
 *   GET  /affiliate/super-affiliate/promo-code/availability/  code, code_type — always 200, read `valid` + `available`
 *   POST /affiliate/super-affiliate/promo-code/               { code_type, code }
 */

/** Request ids are "GA-1F3C9A2B", but older rows come back as "#GA-…". */
const idPath = (id) => encodeURIComponent(String(id ?? ''));

export const affiliateApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* ---- dashboard ---- */
    getAffiliateDashboardCards: builder.query({
      query: (params) => ({ url: '/affiliate/dashboard/cards/', params }),
      providesTags: ['Affiliate'],
    }),
    getAffiliateDashboardTrend: builder.query({
      query: (params) => ({ url: '/affiliate/dashboard/trend/', params }),
      providesTags: ['Affiliate'],
    }),
    getAffiliateRecentRequests: builder.query({
      query: (params) => ({ url: '/affiliate/dashboard/recent-requests/', params }),
      providesTags: ['Affiliate'],
    }),

    /* ---- requests ---- */
    getAffiliateRequestsSummary: builder.query({
      query: () => ({ url: '/affiliate/requests/summary/' }),
      providesTags: ['Affiliate'],
    }),
    getAffiliateRequestOptions: builder.query({
      query: () => ({ url: '/affiliate/requests/options/' }),
      providesTags: ['Affiliate'],
    }),
    getAffiliateRequests: builder.query({
      query: (params) => ({ url: '/affiliate/requests/', params }),
      providesTags: ['Affiliate'],
    }),
    getAffiliateRequest: builder.query({
      query: (id) => ({ url: `/affiliate/requests/${idPath(id)}/` }),
      providesTags: ['Affiliate'],
    }),
    createAffiliateRequest: builder.mutation({
      query: (body) => ({ url: '/affiliate/requests/', method: 'POST', body }),
      invalidatesTags: ['Affiliate'],
    }),

    /* ---- winners ---- */
    getApprovedGiveaways: builder.query({
      query: () => ({ url: '/affiliate/giveaways/approved/' }),
      providesTags: ['Affiliate'],
    }),
    submitGiveawayWinners: builder.mutation({
      query: ({ id, winners }) => ({
        url: `/affiliate/giveaways/${idPath(id)}/winners/`,
        method: 'POST',
        body: { winners },
      }),
      invalidatesTags: ['Affiliate'],
    }),
    getGiveawayWinners: builder.query({
      query: (id) => ({ url: `/affiliate/giveaways/${idPath(id)}/winners/` }),
      providesTags: ['Affiliate'],
    }),
    getAffiliateWinners: builder.query({
      query: (params) => ({ url: '/affiliate/winners/', params }),
      providesTags: ['Affiliate'],
    }),

    /* ---- retainer ---- */
    getAffiliateRetainer: builder.query({
      query: () => ({ url: '/affiliate/retainer/' }),
      providesTags: ['Affiliate', 'Payout'],
    }),
    requestRetainerPayout: builder.mutation({
      query: (body = {}) => ({ url: '/affiliate/retainer/request/', method: 'POST', body }),
      // A retainer payout is a Payout too — the payouts pages list it.
      invalidatesTags: ['Affiliate', 'Payout'],
    }),

    /* ---- the trader's affiliate status, asked when the section opens ---- */
    getAffiliateUserStatus: builder.query({
      query: () => ({ url: '/affiliate/user/status/' }),
      providesTags: ['Affiliate'],
    }),

    /* ---- affiliate marketing ---- */
    getAffiliateMarketingDashboard: builder.query({
      query: (params) => ({ url: '/affiliate/v2/dashboard/', params }),
      providesTags: ['Affiliate'],
    }),
    getAffiliateCodes: builder.query({
      query: (params) => ({ url: '/affiliate/v2/code/list/', params }),
      providesTags: ['Affiliate'],
    }),
    getAffiliateCodeReferrals: builder.query({
      query: (params) => ({ url: '/affiliate/get/code/referred-users/list/', params }),
      providesTags: ['Affiliate'],
    }),
    getAffiliateCodeCountries: builder.query({
      query: (params) => ({ url: '/affiliate/get/countrywise-code/list/', params }),
      providesTags: ['Affiliate'],
    }),
    getCommissionPayments: builder.query({
      query: (params) => ({ url: '/affiliate/v2/commission/payment/', params }),
      providesTags: ['Affiliate'],
    }),

    /* ---- super affiliate ---- */
    submitSuperAffiliateApplication: builder.mutation({
      query: (body) => ({ url: '/affiliate/super-affiliate/application/', method: 'POST', body }),
      invalidatesTags: ['Affiliate'],
    }),
    checkPromoCodeAvailability: builder.query({
      query: ({ code, codeType }) => ({
        url: '/affiliate/super-affiliate/promo-code/availability/',
        params: { code, code_type: codeType },
      }),
      // Asked once per press of Check, never answered from an earlier press.
      keepUnusedDataFor: 0,
    }),
    createSuperAffiliatePromoCode: builder.mutation({
      query: (body) => ({ url: '/affiliate/super-affiliate/promo-code/', method: 'POST', body }),
      invalidatesTags: ['Affiliate'],
    }),
  }),
});

export const {
  useGetAffiliateDashboardCardsQuery,
  useGetAffiliateDashboardTrendQuery,
  useGetAffiliateRecentRequestsQuery,
  useGetAffiliateRequestsSummaryQuery,
  useGetAffiliateRequestOptionsQuery,
  useGetAffiliateRequestsQuery,
  useGetAffiliateRequestQuery,
  useCreateAffiliateRequestMutation,
  useGetApprovedGiveawaysQuery,
  useSubmitGiveawayWinnersMutation,
  useGetGiveawayWinnersQuery,
  useGetAffiliateWinnersQuery,
  useGetAffiliateRetainerQuery,
  useRequestRetainerPayoutMutation,
  useGetAffiliateUserStatusQuery,
  useGetAffiliateMarketingDashboardQuery,
  useGetAffiliateCodesQuery,
  useGetAffiliateCodeReferralsQuery,
  useGetAffiliateCodeCountriesQuery,
  useGetCommissionPaymentsQuery,
  useSubmitSuperAffiliateApplicationMutation,
  useLazyCheckPromoCodeAvailabilityQuery,
  useCreateSuperAffiliatePromoCodeMutation,
} = affiliateApi;

/**
 * An API error as one readable line. The requests endpoints answer
 * `{field: [message]}`, the retainer ones `{detail, code}`, the winners one
 * `{giveaway: "…"}` — so take whichever is there.
 */
export const affiliateErrorMessage = (err, fallback = 'Something went wrong. Please try again.') => {
  const data = err?.data;
  if (!data) return err?.error ? String(err.error) : t(fallback);
  if (typeof data === 'string') return data;
  if (data.detail) return String(data.detail);
  if (data.message) return String(data.message);
  const first = Object.values(data).find((v) => v != null);
  if (Array.isArray(first)) return String(first[0]);
  if (first != null && typeof first !== 'object') return String(first);
  return t(fallback);
};
